import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaExternalLinkAlt,
  FaGoogleDrive,
  FaSyncAlt,
} from "react-icons/fa";

import { AuthContext } from "../../context/AuthContext";
import flaskApi from "../../services/flaskApi";
import { extractErrorMessage } from "../../utils/errorUtils";
import { getRecentGoogleSheets } from "../../utils/googleSheetRecents";

function formatModifiedTime(value) {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const m = parsed.getMonth() + 1;
  const d = parsed.getDate();
  const y = String(parsed.getFullYear()).slice(2);
  const hh = String(parsed.getHours()).padStart(2, "0");
  const mm = String(parsed.getMinutes()).padStart(2, "0");
  const ss = String(parsed.getSeconds()).padStart(2, "0");
  return `${m}/${d}/${y} ${hh}:${mm}:${ss}`;
}

function LinesheetSourceSelector({
  source,
  onSourceChange,
  uploadFile,
  onUploadFileChange,
  selectedGoogleSheet,
  onSelectedGoogleSheetChange,
  uploadLabel = "Linesheet",
  uploadAccept = ".xlsx,.xls",
}) {
  const { user } = useContext(AuthContext);
  const [googleFiles, setGoogleFiles] = useState([]);
  const [serviceAccountEmail, setServiceAccountEmail] = useState("");
  const [loadingGoogleFiles, setLoadingGoogleFiles] = useState(false);
  const [googleFilesLoaded, setGoogleFilesLoaded] = useState(false);
  const [googleFilesError, setGoogleFilesError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showLinkInfo, setShowLinkInfo] = useState(false);

  const recentGoogleSheets = useMemo(
    () => getRecentGoogleSheets(user?.employeeId),
    [user?.employeeId]
  );
  const recentOrder = useMemo(
    () => new Map(recentGoogleSheets.map((item, index) => [item.id, index])),
    [recentGoogleSheets]
  );

  const sortedGoogleFiles = useMemo(
    () =>
      [...googleFiles].sort((left, right) => {
        const leftRank = recentOrder.has(left.id)
          ? recentOrder.get(left.id)
          : Number.MAX_SAFE_INTEGER;
        const rightRank = recentOrder.has(right.id)
          ? recentOrder.get(right.id)
          : Number.MAX_SAFE_INTEGER;

        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }

        const leftModified = new Date(left.modifiedTime || 0).getTime();
        const rightModified = new Date(right.modifiedTime || 0).getTime();
        return rightModified - leftModified;
      }),
    [googleFiles, recentOrder]
  );

  const query = searchText.trim().toLowerCase();
  const filteredGoogleFiles = sortedGoogleFiles.filter((file) => {
    if (!query) {
      return true;
    }

    return [file.name, file.ownerName, file.ownerEmail].some((value) =>
      String(value || "").toLowerCase().includes(query)
    );
  });

  const recentFiles = query
    ? []
    : filteredGoogleFiles.filter((file) => recentOrder.has(file.id));
  const otherFiles = query
    ? filteredGoogleFiles
    : filteredGoogleFiles.filter((file) => !recentOrder.has(file.id));

  const loadGoogleFiles = async () => {
    setLoadingGoogleFiles(true);
    setGoogleFilesError("");

    try {
      const response = await flaskApi.get("/linesheets/google-sheets");
      setGoogleFiles(response.data.files || []);
      setServiceAccountEmail(response.data.serviceAccountEmail || "");
      setGoogleFilesLoaded(true);
    } catch (error) {
      const errorServiceAccountEmail =
        error?.response?.data?.serviceAccountEmail || "";
      if (errorServiceAccountEmail) {
        setServiceAccountEmail(errorServiceAccountEmail);
      }
      const message = await extractErrorMessage(
        error,
        "Unable to load shared Google Sheets."
      );
      setGoogleFilesError(message);
      setGoogleFilesLoaded(true);
    } finally {
      setLoadingGoogleFiles(false);
    }
  };

  useEffect(() => {
    if (source === "google" && !googleFilesLoaded && !loadingGoogleFiles) {
      loadGoogleFiles();
    }
  }, [source, googleFilesLoaded, loadingGoogleFiles]);

  useEffect(() => {
    if (source !== "google") {
      setShowLinkInfo(false);
    }
  }, [source]);

  const handleUploadChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    onUploadFileChange(nextFile);
  };

  const handleGoogleSelection = (file) => {
    if (selectedGoogleSheet?.id === file.id) {
      onSelectedGoogleSheetChange(null);
      return;
    }

    onSelectedGoogleSheetChange(file);
  };

  const renderGoogleFileList = (files) => (
    <div className="google-linesheet-list">
      {files.map((file) => {
        const isSelected = selectedGoogleSheet?.id === file.id;
        const isRecent = recentOrder.has(file.id);

        return (
          <button
            key={file.id}
            type="button"
            className={`google-linesheet-item ${isSelected ? "selected" : ""}`}
            onClick={() => handleGoogleSelection(file)}
          >
            <div className="google-linesheet-item-main">
              <div className="google-linesheet-name-row">
                <div className="google-linesheet-name">{file.name}</div>
                {isRecent ? (
                  <span className="google-linesheet-recent-badge">Recent</span>
                ) : null}
              </div>
              <div className="google-linesheet-meta">
                <span>{formatModifiedTime(file.modifiedTime)}</span>
              </div>
            </div>

            <div className="google-linesheet-item-actions">
              {file.webViewLink ? (
                <a
                  href={file.webViewLink}
                  target="_blank"
                  rel="noreferrer"
                  className="google-linesheet-link"
                  onClick={(event) => event.stopPropagation()}
                >
                  Open <FaExternalLinkAlt />
                </a>
              ) : null}
              {isSelected ? <FaCheckCircle className="selected-check" /> : null}
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="tool-form-group">
      <div className="linesheet-source-toggle">
        <button
          type="button"
          className={`linesheet-source-toggle-button ${source === "google" ? "active" : ""}`}
          onClick={() => onSourceChange("google")}
        >
          <span className="linesheet-source-toggle-title">
            <FaGoogleDrive /> Shared Google Sheet
          </span>
          <span className="linesheet-source-toggle-copy">
            Browse shared Sheets
          </span>
        </button>
        <button
          type="button"
          className={`linesheet-source-toggle-button ${source === "upload" ? "active" : ""}`}
          onClick={() => onSourceChange("upload")}
        >
          <span className="linesheet-source-toggle-title">
            <FaCloudUploadAlt /> Upload Excel
          </span>
          <span className="linesheet-source-toggle-copy">
            Use a local workbook
          </span>
        </button>
      </div>

      {source === "upload" ? (
        <div className="linesheet-source-panel">
          <div className="tool-field-stack">
            <label className="tool-label">{uploadLabel}</label>
            <div className={`file-upload-wrapper ${uploadFile ? "has-file" : ""}`}>
              <input
                type="file"
                accept={uploadAccept}
                className="file-upload-input"
                onClick={(event) => {
                  event.target.value = null;
                }}
                onChange={handleUploadChange}
              />
              <div className="file-upload-text">
                {uploadFile ? (
                  <span className="file-selected-text">
                    <FaCheckCircle /> {uploadFile.name}
                  </span>
                ) : (
                  <>
                    <FaCloudUploadAlt /> <span>Choose File</span>
                  </>
                )}
              </div>
            </div>

            {uploadFile ? (
              <button
                type="button"
                className="tool-link-button"
                onClick={() => onUploadFileChange(null)}
              >
                Clear file
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="linesheet-source-panel google-linesheet-panel">
          <div className="google-linesheet-split">
            <div className="google-linesheet-sidebar">
              <div className="google-share-toolbar">
                <div className="google-share-toolbar-actions">
                  <button
                    type="button"
                    className="tool-btn-secondary"
                    onClick={() => setShowLinkInfo(!showLinkInfo)}
                  >
                    Link a new sheet
                  </button>
                  <button
                    type="button"
                    className="tool-btn-secondary"
                    onClick={loadGoogleFiles}
                    disabled={loadingGoogleFiles}
                  >
                    <FaSyncAlt className={loadingGoogleFiles ? "spin-icon" : ""} />
                    {loadingGoogleFiles ? "Refreshing..." : "Refresh"}
                  </button>
                </div>

                {showLinkInfo ? (
                  <div className="google-share-popover">
                    <div className="google-share-card">
                      <div className="google-share-email">
                        {serviceAccountEmail || "Loading..."}
                      </div>
                      <div className="google-share-steps">
                        <div className="google-share-step">1. Open the sheet -&gt; Share</div>
                        <div className="google-share-step">2. Add the email above</div>
                        <div className="google-share-step">3. Refresh and select</div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {googleFilesError ? (
                <div className="tool-message error">{googleFilesError}</div>
              ) : null}

              <div className="google-selection-card">
                {selectedGoogleSheet ? (
                  <>
                    <div className="google-selection-label">Selected Sheet</div>
                    <div className="google-selection-name">{selectedGoogleSheet.name}</div>
                  </>
                ) : (
                  <div className="google-selection-empty">
                    Choose a shared sheet to continue.
                  </div>
                )}
              </div>
            </div>

            <div className="google-linesheet-browser">
              <div className="google-linesheet-controls">
                <input
                  type="text"
                  className="tool-input"
                  placeholder="Search shared sheets"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </div>

              {googleFilesLoaded && !googleFilesError && filteredGoogleFiles.length === 0 ? (
                <div className="google-linesheet-empty">
                  No shared Google Sheets found yet.
                </div>
              ) : null}

              {!query && recentFiles.length > 0 ? (
                <div className="google-linesheet-group">
                  <div className="google-linesheet-group-label">Recent</div>
                  {renderGoogleFileList(recentFiles)}
                </div>
              ) : null}

              {otherFiles.length > 0 ? (
                <div className="google-linesheet-group">
                  <div className="google-linesheet-group-label">
                    {query ? "Results" : "All Sheets"}
                  </div>
                  {renderGoogleFileList(otherFiles)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LinesheetSourceSelector;
